import { useMemo, type ReactNode } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

export type BoardNode = {
  id: string;
  title: string;
  sub?: string;
  accent?: boolean;
};

export type BoardEdge = {
  from: string;
  to: string;
};

export type BoardChart = {
  kind: 'bar' | 'pie';
  title: string;
  caption?: string;
  unit?: string;
  data: Array<{ name: string; value: number }>;
};

export type MapSegmentTile = {
  name: string;
  value: number;
  pct: number;
  color?: string;
  note?: string;
};

export type MapPayerBubble = {
  id: string;
  label: string;
  rev: number;
  pct: number;
  group: string;
  x: number;
  y: number;
  color?: string;
};

export type MapInvestigationProbe = {
  id: string;
  line: string;
  kind: string;
  status: string;
  claim: string;
  already: string;
  fetch: string;
  close_if: string;
  constraint_class: string;
  ask: string;
  finding?: string;
};

export type MapDrillStone = {
  title: string;
  status: string;
  finding: string;
};

export type MapInvestigationLine = {
  name: string;
  heat: string;
  bucket?: string;
  probes: MapInvestigationProbe[];
};

export type MapInvestigations = {
  ticker?: string;
  as_of?: string;
  how_to_use?: string;
  lines?: MapInvestigationLine[];
  probes?: MapInvestigationProbe[];
  stones?: MapDrillStone[];
  drill?: string;
  drilled_at?: string;
};

export type CohortBoardPayload = {
  object: string;
  as_of?: string;
  headline?: string;
  picture?: string;
  layout?: 'revenue_map' | 'cohort' | 'research_ledger' | 'sec_graph';
  stats?: Array<{ value: string; label: string; tone?: string }>;
  join?: { nodes: BoardNode[]; edges: BoardEdge[] };
  businesses?: { nodes: BoardNode[]; edges: BoardEdge[] };
  charts?: BoardChart[];
  revenue_segments?: MapSegmentTile[];
  payer_bubbles?: MapPayerBubble[];
  investigations?: MapInvestigations;
  mix?: Array<{ people_think: string; files_show: string }>;
  overreach?: Array<{ claim: string; because?: string }>;
  watch?: string;
  memo?: string;
  drill?: string;
  question?: string;
  frame?: {
    restated_claim?: string;
    identity?: string;
    constraint_class?: string;
    response_kind?: string;
  };
  findings?: Array<Record<string, string>>;
  needs?: Array<Record<string, string>>;
  hops?: Array<Record<string, string>>;
  sources?: string[];
  counts?: Record<string, number>;
  type_counts?: Record<string, number>;
  graph_nodes?: Array<{
    id: string;
    type: string;
    label: string;
    value?: string | number | null;
    unit?: string;
    period?: string;
    form?: string;
    file?: string;
    source?: string;
    url?: string | null;
    restated?: string;
    research?: {
      restated?: string;
      identity?: string;
      impacts?: Array<{ kind?: string; node_id?: string; label: string; how?: string }>;
      comes_from?: Array<{ kind?: string; node_id?: string; label: string; how?: string }>;
      related?: Array<{ kind?: string; node_id?: string; label: string; how?: string }>;
      connects_to?: Array<{ kind?: string; node_id?: string; label: string; how?: string }>;
      tied_data?: string[];
      acquire?: Array<{
        via?: string;
        what?: string;
        where?: string;
        tool?: string;
        if_missing?: string;
      }>;
      close_if?: string;
      hole?: string;
      reasoning?: string;
      world_reasoning?: string;
      world?: Array<{
        kind?: string;
        label?: string;
        ticker?: string;
        how?: string;
        where?: string;
        tool?: string;
        file?: string;
      }>;
    } | null;
  }>;
  graph_edges?: Array<{ from: string; to: string; type: string }>;
  cik?: string;
  company_name?: string;
  harvest_source?: string;
  exhaust?: {
    legal_name?: string;
    queries?: string[];
    stage?: string;
    hit?: number;
    empty?: number;
    blocked?: number;
    listed?: number;
    indexed_not_fetched?: number;
    skipped_macros_n?: number;
    calls?: Array<Record<string, string | number>>;
  };
  finished?: boolean;
};

type Props = {
  board: CohortBoardPayload;
  memoSlot?: ReactNode;
};

const PIE_COLORS = ['#b45309', '#d1d5db'];
const BAR_FILL = '#111827';
const NODE_W = 158;
const NODE_H = 52;

function layoutDag(nodes: BoardNode[], edges: BoardEdge[]) {
  const incoming = new Set(edges.map((e) => e.to));
  const children = new Map<string, string[]>();
  for (const e of edges) {
    const list = children.get(e.from) ?? [];
    list.push(e.to);
    children.set(e.from, list);
  }
  const rank = new Map<string, number>();
  const roots = nodes.filter((n) => !incoming.has(n.id)).map((n) => n.id);
  const queue = [...(roots.length ? roots : nodes.slice(0, 1).map((n) => n.id))];
  for (const id of queue) rank.set(id, 0);
  for (let i = 0; i < queue.length; i++) {
    const id = queue[i];
    const r = rank.get(id) ?? 0;
    for (const child of children.get(id) ?? []) {
      const next = r + 1;
      if (!rank.has(child) || next > (rank.get(child) ?? 0)) {
        rank.set(child, next);
        queue.push(child);
      }
    }
  }
  for (const n of nodes) {
    if (!rank.has(n.id)) rank.set(n.id, 0);
  }
  const byRank = new Map<number, BoardNode[]>();
  for (const n of nodes) {
    const r = rank.get(n.id) ?? 0;
    const list = byRank.get(r) ?? [];
    list.push(n);
    byRank.set(r, list);
  }
  const ranks = [...byRank.keys()].sort((a, b) => a - b);
  const gapX = 14;
  const gapY = 56;
  const pad = 8;
  const pos = new Map<string, { x: number; y: number }>();
  let width = pad * 2;
  ranks.forEach((r) => {
    const row = byRank.get(r) ?? [];
    const rowW = row.length * NODE_W + Math.max(0, row.length - 1) * gapX;
    width = Math.max(width, rowW + pad * 2);
    row.forEach((n, i) => {
      pos.set(n.id, { x: pad + i * (NODE_W + gapX), y: pad + r * (NODE_H + gapY) });
    });
  });
  ranks.forEach((r) => {
    const row = byRank.get(r) ?? [];
    const rowW = row.length * NODE_W + Math.max(0, row.length - 1) * gapX;
    const offset = Math.max(0, (width - rowW) / 2 - pad);
    row.forEach((n) => {
      const p = pos.get(n.id);
      if (p) p.x += offset;
    });
  });
  const height = pad * 2 + ranks.length * NODE_H + Math.max(0, ranks.length - 1) * gapY;
  return { pos, width, height };
}

function Dag({
  title,
  nodes,
  edges,
}: {
  title: string;
  nodes: BoardNode[];
  edges: BoardEdge[];
}) {
  const { pos, width, height } = useMemo(() => layoutDag(nodes, edges), [nodes, edges]);
  if (!nodes.length) return null;
  return (
    <div>
      <h3 className="mb-2 text-sm font-semibold text-gray-900">{title}</h3>
      <svg
        width="100%"
        viewBox={`0 0 ${width} ${height}`}
        className="max-h-[280px]"
        role="img"
        aria-label={title}
      >
        {edges.map((e) => {
          const a = pos.get(e.from);
          const b = pos.get(e.to);
          if (!a || !b) return null;
          return (
            <line
              key={`${e.from}-${e.to}`}
              x1={a.x + NODE_W / 2}
              y1={a.y + NODE_H}
              x2={b.x + NODE_W / 2}
              y2={b.y}
              stroke="#9ca3af"
              strokeWidth={1.5}
            />
          );
        })}
        {nodes.map((n) => {
          const p = pos.get(n.id);
          if (!p) return null;
          return (
            <g key={n.id}>
              <rect
                x={p.x}
                y={p.y}
                width={NODE_W}
                height={NODE_H}
                rx={6}
                fill={n.accent ? '#111827' : '#f3f4f6'}
                stroke={n.accent ? '#111827' : '#e5e7eb'}
              />
              <text
                x={p.x + 10}
                y={p.y + 20}
                fill={n.accent ? '#ffffff' : '#111827'}
                fontSize={11}
                fontWeight={600}
              >
                {n.title}
              </text>
              {n.sub ? (
                <text x={p.x + 10} y={p.y + 38} fill={n.accent ? '#d1d5db' : '#6b7280'} fontSize={10}>
                  {n.sub.length > 28 ? `${n.sub.slice(0, 27)}…` : n.sub}
                </text>
              ) : null}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function statClass(tone?: string) {
  if (tone === 'danger') return 'text-rose-700';
  if (tone === 'warning') return 'text-amber-800';
  return 'text-gray-900';
}

function BoardChart({ chart }: { chart: BoardChart }) {
  if (chart.kind === 'pie') {
    return (
      <div>
        <h3 className="mb-2 text-sm font-semibold text-gray-900">{chart.title}</h3>
        <div className="h-[200px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={chart.data} dataKey="value" nameKey="name" innerRadius={48} outerRadius={72} paddingAngle={2}>
                {chart.data.map((entry, i) => (
                  <Cell key={entry.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value: number) => [`$${value}B`, '']} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        {chart.caption ? <p className="mt-1 text-xs leading-5 text-gray-500">{chart.caption}</p> : null}
      </div>
    );
  }
  return (
    <div>
      <h3 className="mb-2 text-sm font-semibold text-gray-900">{chart.title}</h3>
      <div className="h-[200px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chart.data} margin={{ top: 8, right: 8, left: 0, bottom: 24 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#6b7280' }} interval={0} />
            <YAxis tick={{ fontSize: 10, fill: '#6b7280' }} width={40} />
            <Tooltip
              formatter={(value: number) => [
                chart.unit ? `${value} ${chart.unit}` : String(value),
                chart.title,
              ]}
            />
            <Bar dataKey="value" fill={BAR_FILL} radius={[4, 4, 0, 0]} maxBarSize={48} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      {chart.caption ? <p className="mt-1 text-xs leading-5 text-gray-500">{chart.caption}</p> : null}
    </div>
  );
}

export function CohortBoard({ board, memoSlot }: Props) {
  const pie = board.charts?.filter((c) => c.kind === 'pie') ?? [];
  const bars = board.charts?.filter((c) => c.kind === 'bar') ?? [];
  const leadBar = bars[0];
  const restBars = bars.slice(1);

  return (
    <div className="space-y-6" data-cohort-board>
      {board.object ? (
        <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
          {board.object}
          {board.as_of ? ` · ${board.as_of}` : ''}
        </p>
      ) : null}
      {board.headline ? (
        <p className="text-[15px] leading-7 text-gray-800">{board.headline}</p>
      ) : null}

      {board.picture ? (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-950">
          {board.picture}
        </div>
      ) : null}

      {board.stats && board.stats.length > 0 ? (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {board.stats.map((stat) => (
            <div key={stat.label} className="rounded-2xl border border-gray-200 px-3 py-3">
              <p className={`text-lg font-semibold tracking-tight ${statClass(stat.tone)}`}>
                {stat.value}
              </p>
              <p className="mt-0.5 text-xs leading-4 text-gray-500">{stat.label}</p>
            </div>
          ))}
        </div>
      ) : null}

      {board.join?.nodes?.length ? (
        <Dag title="How the files sit next to each other" nodes={board.join.nodes} edges={board.join.edges} />
      ) : null}

      {board.businesses?.nodes?.length ? (
        <Dag title="Do not mix these businesses" nodes={board.businesses.nodes} edges={board.businesses.edges} />
      ) : null}

      {pie.length || bars.length ? (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {pie.map((chart) => (
            <BoardChart key={chart.title} chart={chart} />
          ))}
          {leadBar ? <BoardChart key={leadBar.title} chart={leadBar} /> : null}
        </div>
      ) : null}

      {restBars.length > 0 ? (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {restBars.map((chart) => (
            <BoardChart key={chart.title} chart={chart} />
          ))}
        </div>
      ) : null}

      {board.mix && board.mix.length > 0 ? (
        <div>
          <h3 className="mb-2 text-sm font-semibold text-gray-900">Sold vs the file</h3>
          <div className="overflow-hidden rounded-2xl border border-gray-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-xs font-medium text-gray-500">
                  <th className="px-3 py-2">Priced as</th>
                  <th className="px-3 py-2">File shows</th>
                </tr>
              </thead>
              <tbody>
                {board.mix.map((row) => (
                  <tr key={row.people_think} className="border-t border-gray-100 align-top">
                    <td className="px-3 py-2 text-gray-800">{row.people_think}</td>
                    <td className="px-3 py-2 text-gray-600">{row.files_show}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {board.overreach && board.overreach.length > 0 ? (
        <details className="rounded-2xl border border-gray-200 px-4 py-2">
          <summary className="cursor-pointer text-sm font-medium text-gray-700">
            Inferred — do not use
          </summary>
          <ul className="mt-2 space-y-2 pb-2 text-sm text-gray-600">
            {board.overreach.map((item) => (
              <li key={item.claim}>
                <span className="font-medium text-gray-800">{item.claim}</span>
                {item.because ? ` — ${item.because}` : ''}
              </li>
            ))}
          </ul>
        </details>
      ) : null}

      {board.watch ? (
        <p className="text-sm leading-6 text-gray-600">
          <span className="font-semibold text-gray-800">Watch. </span>
          {board.watch}
        </p>
      ) : null}

      {memoSlot}
    </div>
  );
}
